import { useNavigate } from '@rocketlane/rocket-router';
import { useCallback } from 'react';
import { ROUTE_PATHS } from 'routes/route-paths';

const isModuleFederation =
  typeof process !== 'undefined' && process.env.IS_MODULE_FEDERATION === 'true';

const getAccountDetailRoute = (companyId: number, projectId?: number) => {
  // If projectId is provided (module federation mode), use project routes
  if (projectId != null || isModuleFederation) {
    const id = projectId ?? companyId;

    return `/projects/${id}/meeting-pages`;
  }

  return `/${ROUTE_PATHS.ACCOUNTS}/${ROUTE_PATHS.ACCOUNT_DETAIL.replace(
    ':accountId',
    companyId.toString() ?? '',
  )}`;
};
const getBaseMeetingPageRoute = (
  companyId: number,
  meetingId?: number,
  projectId?: number,
) => {
  const baseRoute = getAccountDetailRoute(companyId, projectId);

  // If projectId is provided (module federation mode), use project routes
  if (projectId != null || isModuleFederation) {
    return meetingId ? `${baseRoute}/${meetingId}` : baseRoute;
  }

  const meetingsPath = meetingId
    ? ROUTE_PATHS.ACCOUNT_DETAIL_MEETINGS.replace(
        ':meetingId?',
        meetingId.toString(),
      )
    : ROUTE_PATHS.ACCOUNT_DETAIL_MEETINGS.replace('/:meetingId?', '');

  return `${baseRoute}/${meetingsPath}`;
};
const getMeetingPageListRoute = (companyId: number, projectId?: number) => {
  return getBaseMeetingPageRoute(companyId, undefined, projectId);
};

export const getMeetingPageRoute = (
  companyId: number,
  meetingId: number,
  projectId?: number,
) => {
  return getBaseMeetingPageRoute(companyId, meetingId, projectId);
};
const getMeetingPageEditRoute = (
  companyId: number,
  meetingId: number,
  projectId?: number,
) => {
  const baseRoute = getMeetingPageRoute(companyId, meetingId, projectId);

  // If projectId is provided (module federation mode), use project routes
  if (projectId != null || isModuleFederation) {
    return `${baseRoute}/edit`;
  }

  return `${baseRoute}/${ROUTE_PATHS.ACCOUNT_DETAIL_MEETING_EDIT}`;
};

export const useMeetingPagesRouteNavigations = (projectId?: number) => {
  const navigate = useNavigate();
  const navigateToMeetingList = useCallback(
    (companyId: number) => {
      navigate(getMeetingPageListRoute(companyId, projectId));
    },
    [navigate, projectId],
  );
  const navigateToMeeting = useCallback(
    (companyId: number, meetingId: number) => {
      navigate(getMeetingPageRoute(companyId, meetingId, projectId));
    },
    [navigate, projectId],
  );
  const navigateToMeetingEdit = useCallback(
    (companyId: number, meetingId: number) => {
      navigate(getMeetingPageEditRoute(companyId, meetingId, projectId));
    },
    [navigate, projectId],
  );

  return {
    navigateToMeetingList,
    navigateToMeeting,
    navigateToMeetingEdit,
  };
};
